import JustinFlom from "@/assets/images/adcelerate/justin-flom.png";
import Button from "@/components/ui/button";
import useCountUp from "@/hooks/useCountUp";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { formatToK, formatWithCommas } from "@/utils/formatNumber";
import scrollTo from "@/utils/scrollTo";
import startTypewriter from "@/utils/typewriterAnimation";
import {
  ArrowDownRight,
  Flag,
  Heart,
  MessageSquare,
  TrendingUp,
  UserPlus,
} from "lucide-react";
import { useEffect } from "react";

export default function WhatWeDo() {
  const { ref, isVisible } = useScrollReveal();

  const followers = useCountUp(2377, 2000);
  const comments = useCountUp(9874, 2000);
  const likes = useCountUp(15000, 2000);
  const reach = useCountUp(256000, 2000);

  useEffect(() => {
    startTypewriter({
      elementId: "whatWeDoTypewriter",
      words: ["WHAT WE DO", "WE BOOST CONTENTS"],
    });
  }, []);

  return (
    <div
      ref={ref}
      className="bg-[linear-gradient(to_bottom,var(--primary),var(--tertiary))] "
    >
      <div
        className={`${
          isVisible ? "bounce-up" : ""
        } relative overflow-hidden 4xl:max-w-[100rem] 4xl:mx-auto`}
      >
        <div className="p-10 lg:p-20 xl:p-32 py-20 lg:py-0 flex flex-col lg:flex-row justify-between items-center lg:h-[700px] 5xl:max-w-[100rem] 5xl:mx-auto">
          <div className="space-y-6 text-left lg:w-[50%] text-white z-40">
            <div className="space-y-6 text-left">
              <h1 className="text-4xl md:text-6xl font-medium adce-hero">
                <span id="whatWeDoTypewriter"></span>
                <span className="border-r-8 border-white animate-pulse ml-1"></span>
              </h1>
              <div className="space-y-8 text-left pt-[570px] xs:pt-[580px] md:pt-[590px] lg:pt-0">
                <p className="text-lg">
                  We partner with top Creators and thoughtfully curate their top
                  videos into repeatable success stories across several new
                  platforms that generate additional revenue that you’d
                  otherwise leave on the table without us.
                </p>
                <p className="text-lg">
                  In several cases, the platforms we partner with require you to
                  go through a partner like us to even access them, having
                  established companies and structures in place.
                </p>
              </div>
            </div>
            <Button
              className="group relative rounded-md shadow bg-primary text-white py-6 w-52 text-base flex items-center justify-center gap-2 overflow-hidden"
              onClick={() => scrollTo("who-do-we-do-it-for")}
            >
              <span className="transform transition-transform duration-300 group-hover:translate-x-5">
                Who we do it for
              </span>
              <ArrowDownRight className="transform transition-transform duration-300 group-hover:-translate-x-[131px]" />
            </Button>
          </div>
        </div>
        {/* Followers */}
        <div className="rounded-[20px] py-3 px-4 bg-white bg-opacity-80 flex items-center gap-4 absolute top-44 md:top-52 lg:top-24 xl:top-20 -right-48 md:-right-20 lg:-right-44 xl:-right-10 w-[500px]">
          <div className="rounded-full p-2 bg-[#7FBA00] w-fit">
            <UserPlus />
          </div>
          <div className="space-y-1">
            <h1 className="text-3xl font-medium" ref={followers.ref}>
              {formatWithCommas(followers.count)}
            </h1>
            <p className="text-gray-500 text-sm">
              followers gained after signing in{" "}
            </p>
          </div>
        </div>
        {/* Likes */}
        <div className="rounded-[20px] p-4 bg-white absolute top-[280px] md:top-[315px] lg:top-[201px] xl:top-[185px] -right-40 xs:-right-36 md:-right-20 lg:-right-32 xl:-right-20 w-80 xl:h-52 space-y-4 lg:space-y-5 xl:space-y-7">
          <div className="flex items-center gap-3">
            <div className="rounded-full p-2 bg-[#FF5053] w-fit text-white">
              <Heart className="w-5 h-5" />
            </div>
            <p className="text-gray-500 text-lg">Likes</p>
          </div>
          <h1 className="text-5xl pl-4 font-medium" ref={likes.ref}>
            {formatToK(likes.count)}k
          </h1>
          <div className="flex items-center gap-4">
            <div className="rounded-full py-1 px-3 flex items-center gap-2 bg-[#196A0B] text-[#196A0B] bg-opacity-20 font-bold">
              <TrendingUp className="w-4 h-4" />
              2.1%
            </div>
            <p className="text-gray-400">vs last 7 days</p>
          </div>
        </div>
        {/* Comments */}
        <div className="rounded-[20px] p-4 bg-white bg-opacity-50 absolute top-[280px] md:top-[315px] lg:top-[201px] xl:top-[185px] right-44 xs:right-[190px] md:right-[260px] lg:right-[210px] xl:right-[270px] w-44 sm:w-48 md:w-60 space-y-4">
          <div className="flex items-center gap-3">
            <div className="rounded-full p-2 bg-[#6D6ED0] w-fit text-white">
              <MessageSquare className="w-5 h-5" />
            </div>
            <p className="text-gray-500 text-lg">Comments</p>
          </div>
          <h1 className="text-5xl pl-4 font-medium" ref={comments.ref}>
            {formatWithCommas(comments.count)}
          </h1>
          <div className="rounded-full w-fit py-1 px-3 flex items-center gap-2 bg-[#196A0B] text-[#196A0B] bg-opacity-20 font-bold">
            <TrendingUp className="w-4 h-4" />
            2.1%
          </div>
        </div>
        {/* Reach */}
        <div className="rounded-[20px] p-4 bg-white absolute top-[480px] md:top-[520px] lg:top-[410px] xl:top-[415px] -right-40 xs:-right-36 lg:-right-24 xl:-right-10 w-80 space-y-5 xl:space-y-8">
          <div className="flex items-center gap-3">
            <div className="rounded-full p-2 bg-[#FCD116] w-fit text-white">
              <Flag className="w-5 h-5" />
            </div>
            <p className="text-gray-500 text-lg">Reach</p>
          </div>
          <h1 className="text-5xl pl-4 font-medium" ref={reach.ref}>
            {formatToK(reach.count)}k
          </h1>
          <div className="flex items-center gap-4">
            <div className="rounded-full py-1 px-3 flex items-center gap-2 bg-[#196A0B] text-[#196A0B] bg-opacity-20 font-bold">
              <TrendingUp className="w-4 h-4" />
              2.1%
            </div>
            <p className="text-gray-400">vs last 7 days</p>
          </div>
        </div>
        {/* Image */}
        <div className="rounded-[20px] absolute top-[480px] md:top-[520px] lg:top-[410px] xl:top-[415px] right-44 xs:right-48 md:right-[195px] lg:right-60 xl:right-80 w-44 md:w-52 space-y-4">
          <img src={JustinFlom} alt="Justin Flom" />
        </div>
      </div>
    </div>
  );
}
